import { ButtonHTMLAttributes, FC, forwardRef, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

export type Option = {
    id: string | number;
    title: string;
};

type SelectProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'> & {
    options: Option[];
    onChange?: (option: Option) => void;
    defaultSelected?: Option['id'];
    required?: boolean;
};

const Select: FC<SelectProps> = forwardRef<HTMLButtonElement, SelectProps>(
    ({ options, defaultSelected, name, id, onChange, required = false }: SelectProps, ref) => {
        const [isOpen, setIsOpen] = useState(false);
        const [selectedOption, setSelectedOption] = useState<Option | null>(null);

        const selectedOptionRef = useRef(false);

        const handleClick = () => {
            setIsOpen((prevState) => !prevState);
        };

        const handleSelect = (option: Option) => {
            selectedOptionRef.current = true;
            setSelectedOption(option);

            setIsOpen(false);
        };

        useEffect(() => {
            let option = null;

            if (options.length !== 0) {
                option = options[0];
            }

            if (!!defaultSelected) {
                option = options.find((option) => option.id === defaultSelected);
            }

            if (option) {
                setSelectedOption(option);
                onChange && onChange(option);
            }
        }, [options, defaultSelected, onChange]);

        useEffect(() => {
            if (onChange && selectedOption && selectedOptionRef.current) {
                onChange(selectedOption);
            }

            if (selectedOptionRef.current) {
                selectedOptionRef.current = false;
            }
        }, [selectedOption, onChange]);

        return (
            <div className="mt-1 relative">
                <input type="hidden" defaultValue={selectedOption?.id} name={name} id={id} required={required} />
                <button
                    ref={ref}
                    type="button"
                    className="relative w-full bg-white border border-gray-300 rounded-md 
                    shadow-sm pl-3 pr-10 py-2 text-left cursor-default focus:outline-none 
                    focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 bg-gray-100"
                    aria-haspopup="listbox"
                    aria-expanded="true"
                    aria-labelledby="listbox-label"
                    onClick={handleClick}
                >
                    <span className="flex items-center">
                        <span className="block truncate">{selectedOption?.title}</span>
                    </span>
                    <span className="ml-3 absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                        <svg
                            className="h-5 w-5 text-gray-400"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path
                                fillRule="evenodd"
                                d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                                clipRule="evenodd"
                            />
                        </svg>
                    </span>
                </button>

                {/*<!--
                      Select popover, show/hide based on select state.
                
                      Entering: ""
                        From: ""
                        To: ""
                      Leaving: "transition ease-in duration-100"
                        From: "opacity-100"
                        To: "opacity-0"
                    -->*/}
                {isOpen && (
                    <ul
                        className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-56 rounded-md py-1 
                            text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none"
                        tabIndex={-1}
                        role="listbox"
                        aria-labelledby="listbox-label"
                        aria-activedescendant="listbox-option-3"
                    >
                        {/*<!--
                          Select option, manage highlight styles based on mouseenter/mouseleave and keyboard navigation.
                  
                          Highlighted: "text-white bg-indigo-600", Not Highlighted: "text-gray-900"
                        -->*/}
                        {options.map((option) => (
                            <li
                                key={option.id}
                                className="text-gray-900 cursor-pointer select-none relative py-2 pl-3 pr-9 
                                hover:bg-gray-50"
                                role="option"
                                onClick={() => handleSelect(option)}
                            >
                                <div className="flex items-center">
                                    {/*<!-- Selected: "font-semibold", Not Selected: "font-normal" -->*/}
                                    <span
                                        className={classNames('block truncate', {
                                            'font-normal': selectedOption?.id !== option.id,
                                            'font-semibold': selectedOption?.id === option.id,
                                        })}
                                    >
                                        {option.title}
                                    </span>
                                </div>

                                {/*<!--
                                  Checkmark, only display for selected option.
                        
                                  Highlighted: "text-white", Not Highlighted: "text-indigo-600"
                                -->*/}
                                {selectedOption?.id === option.id && (
                                    <span className="text-indigo-600 absolute inset-y-0 right-0 flex items-center pr-4">
                                        <svg
                                            className="h-5 w-5"
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                            aria-hidden="true"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        );
    },
);

Select.displayName = 'Select';

export default Select;
